import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Layers, ChevronRight, Phone, Mail, Edit, Loader, X, UserX, Trash2, Users, ShieldCheck } from 'lucide-react';
import ExpandableSearchBar from '../components/ExpandableSearchBar';
import { floorsApi, employeesApi, departmentsApi } from '../services/api';
import { useTheme } from '../context/ThemeContext'; // Added for currentTheme

function FloorManagement() {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.textColor === '#FFFFFF';
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Team Details Modal State
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Filter States
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    teamStatus: 'All',
    managementStatus: 'All'
  });
  const [tempFilters, setTempFilters] = useState(activeFilters);

  // Sync tempFilters when modal opens
  useEffect(() => {
    if (isFilterModalOpen) {
      setTempFilters(activeFilters);
    }
  }, [isFilterModalOpen, activeFilters]);

  // Fetch floors on mount
  useEffect(() => {
    fetchFloors();
    fetchDepsAndEmps();
  }, []);

  const fetchDepsAndEmps = async () => {
    try {
      const [depts, emps] = await Promise.all([
        departmentsApi.getAllDepartments(),
        employeesApi.getAllEmployees()
      ]);
      setDepartments(depts || []);
      setEmployees(emps || []);
    } catch (err) {
      console.error('Failed to fetch departments or employees', err);
    }
  };

  // Auto-select first team when floor changes
  useEffect(() => {
    if (selectedFloor?.teams?.length > 0) {
      handleTeamClick(selectedFloor.teams[0]);
    } else {
      setSelectedTeam(null);
      setTeamMembers([]);
    }
  }, [selectedFloor]);

  const fetchFloors = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await floorsApi.getAllFloors();
      console.log('Raw response:', response);

      // ✅ Check if response is valid
      let data = response;

      // If response is a string, try to parse it
      if (typeof response === 'string') {
        try {
          data = JSON.parse(response);
        } catch (parseError) {
          console.error('JSON Parse Error:', parseError);
          console.error('Response string:', response.substring(0, 500)); // Log first 500 chars
          throw new Error('Invalid JSON response from server');
        }
      }

      // ✅ Handle different response structures
      const floorsData = Array.isArray(data) ? data : (data.floors || data.data || []);

      console.log('Processed floors:', floorsData);
      setFloors(floorsData);

      if (floorsData.length > 0) {
        setSelectedFloor(floorsData[0]);
      }
    } catch (error) {
      console.error('Error fetching floors:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      setError(error.message || 'Failed to load floors');
      setFloors([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFloorClick = (floor) => {
    setSelectedFloor(floor);
  };

  const getAvatarUrl = (name) => {
    const initial = name?.charAt(0).toUpperCase() || 'A';
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&size=150`;
  };

  const handleTeamClick = async (team) => {
    setSelectedTeam(team);
    setLoadingMembers(true);
    try {
      // Use employeesApi to fetch members
      const members = await employeesApi.getEmployeesByTeam(team.id);
      setTeamMembers(members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
      setTeamMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const closeTeamModal = () => {
    setSelectedTeam(null);
    setTeamMembers([]);
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [floorToDelete, setFloorToDelete] = useState(null);

  const handleDeleteFloor = (e, floorId) => {
    e.stopPropagation();
    setFloorToDelete(floorId);
    setShowDeleteModal(true);
  };

  const confirmDeleteFloor = async () => {
    if (!floorToDelete) return;

    try {
      setLoading(true);
      await floorsApi.deleteFloor(floorToDelete);
      const updatedFloors = floors.filter(f => f.id !== floorToDelete);
      setFloors(updatedFloors);
      if (selectedFloor?.id === floorToDelete) {
        setSelectedFloor(updatedFloors.length > 0 ? updatedFloors[0] : null);
      }
      setShowDeleteModal(false);
      setFloorToDelete(null);
    } catch (error) {
      console.error('Error deleting floor:', error);
      alert('Failed to delete floor');
    } finally {
      setLoading(false);
    }
  };

  // Edit Modal States
  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editRole, setEditRole] = useState(null); // 'Head' | 'Manager'
  const [editDepartmentId, setEditDepartmentId] = useState('');
  const [editEmployeeId, setEditEmployeeId] = useState('');
  const [editSearchTerm, setEditSearchTerm] = useState('');
  const [showEditDeptDropdown, setShowEditDeptDropdown] = useState(false);
  const [showEditEmpDropdown, setShowEditEmpDropdown] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);

  const handleEditClick = (role) => {
    setEditRole(role);
    setEditSearchTerm('');
    setShowEditDeptDropdown(false);
    setShowEditEmpDropdown(false);
    if (role === 'Head') {
      setEditDepartmentId(selectedFloor.floorHeadDepartment?.id || '');
      setEditEmployeeId(selectedFloor.floorHead?.id || '');
    } else {
      setEditDepartmentId(selectedFloor.floorManagerDepartment?.id || '');
      setEditEmployeeId(selectedFloor.floorManager?.id || '');
    }
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    if (!editDepartmentId || !editEmployeeId) {
      alert('Please select both department and employee');
      return;
    }
    const empData = employees.find(e => e.id === editEmployeeId);
    if (!empData) return;

    try {
      setSavingEdit(true);
      const updatePayload = {
        name: selectedFloor.name,
        floorHeadDepartmentId: editRole === 'Head' ? editDepartmentId : selectedFloor.floorHeadDepartment?.id,
        floorHeadEmployeeId: editRole === 'Head' ? editEmployeeId : selectedFloor.floorHead?.id,
        floorManagerDepartmentId: editRole === 'Manager' ? editDepartmentId : selectedFloor.floorManagerDepartment?.id,
        floorManagerEmployeeId: editRole === 'Manager' ? editEmployeeId : selectedFloor.floorManager?.id,
        teamIds: selectedFloor.teams?.map(t => t.id) || []
      };

      await floorsApi.updateFloor(selectedFloor.id, updatePayload);

      // Update selected floor locally
      setSelectedFloor(prev => ({
        ...prev,
        [editRole === 'Head' ? 'floorHeadDepartment' : 'floorManagerDepartment']: departments.find(d => d.id === editDepartmentId),
        [editRole === 'Head' ? 'floorHead' : 'floorManager']: empData
      }));

      setShowEditModal(false);
      fetchFloors(); // refresh the list
    } catch (error) {
      console.error('Error updating floor:', error);
      alert('Failed to update floor');
    } finally {
      setSavingEdit(false);
    }
  };

  const filteredEditEmployees = employees.filter(emp =>
    emp.name.toLowerCase().includes(editSearchTerm.toLowerCase()) ||
    (emp.employeeId && emp.employeeId.toLowerCase().includes(editSearchTerm.toLowerCase()))
  );

  // Filter floors based on search and active filters
  const filteredFloors = floors.filter(floor => {
    const matchesSearch = floor.name?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesTeam =
      activeFilters.teamStatus === 'All' ||
      (activeFilters.teamStatus === 'Has Teams' && floor.teams?.length > 0) ||
      (activeFilters.teamStatus === 'No Teams' && (!floor.teams || floor.teams.length === 0));

    const matchesManagement =
      activeFilters.managementStatus === 'All' ||
      (activeFilters.managementStatus === 'Has Management' && (floor.floorHead || floor.floorManager)) ||
      (activeFilters.managementStatus === 'Missing Info' && (!floor.floorHead || !floor.floorManager));

    return matchesSearch && matchesTeam && matchesManagement;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader className="w-8 h-8 animate-spin" style={{ color: currentTheme.color }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="text-red-500 text-center">
          <p className="text-xl font-semibold mb-2">Error Loading Floors</p>
          <p className="text-sm text-gray-600">{error}</p>
        </div>
        <button
          onClick={fetchFloors}
          className="px-6 py-2 text-white rounded-lg hover:opacity-90 transition-colors"
          style={{ backgroundColor: currentTheme.color }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className={`space-y-6 transition-colors duration-300 ${isDark ? 'min-h-screen' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Floor</h2>
        <button
          onClick={() => navigate('/floor/add')}
          className="px-6 py-3 text-white rounded-lg hover:opacity-90 transition-colors flex items-center space-x-2"
          style={{ backgroundColor: currentTheme.color }}
        >
          <Layers className="w-5 h-5" />
          <span>Add Floor</span>
        </button>
      </div>

      {/* Search Bar and Filter */}
      <div className="flex items-center gap-3">
        <ExpandableSearchBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          placeholder="Search by floor name"
        />
        <button
          onClick={() => setIsFilterModalOpen(true)}
          className={`p-3 border-2 rounded-lg transition-colors flex-shrink-0 ${
            (activeFilters.teamStatus !== 'All' || activeFilters.managementStatus !== 'All') 
              ? (isDark ? 'bg-white/10 border-white/20' : 'bg-teal-50 border-teal-200') 
              : (isDark ? 'bg-[#1e293b] border-white/5 hover:bg-white/5' : 'border-gray-200 hover:bg-gray-50')
          }`}
        >
          <Filter className={`w-5 h-5 ${
            (activeFilters.teamStatus !== 'All' || activeFilters.managementStatus !== 'All') 
              ? (isDark ? 'text-white' : 'text-teal-600') 
              : (isDark ? 'text-gray-400' : 'text-gray-600')
          }`} />
        </button>
      </div>

      {/* Main Content */}
      {filteredFloors.length === 0 ? (
        <div className={`text-center py-20 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          <p>No floors found</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Floor Selection List - Left Side */}
          <div className="lg:col-span-3 space-y-3">
            {filteredFloors.map((floor) => (
              <div
                key={floor.id}
                onClick={() => handleFloorClick(floor)}
                className={`cursor-pointer relative overflow-hidden rounded-xl border-l-4 transition-all duration-200 p-4 ${
                  selectedFloor?.id === floor.id
                    ? 'shadow-md'
                    : isDark ? 'border-white/5 bg-white/5 hover:bg-white/10' : 'border-gray-200 bg-white hover:shadow-sm'
                }`}
                style={{
                  borderLeftColor: selectedFloor?.id === floor.id ? currentTheme.color : (isDark ? '#334155' : '#E5E7EB'),
                  backgroundColor: selectedFloor?.id === floor.id ? (isDark ? 'rgba(255,255,255,0.05)' : `${currentTheme.color}08`) : undefined
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className={`text-base font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-800'}`}>{floor.name}</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold" style={{ color: currentTheme.color }}>{floor.teams?.length || 0}</span>
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    <button
                      className="px-3 py-1.5 text-xs font-medium text-white rounded-md transition-colors"
                      style={{ backgroundColor: currentTheme.color }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate('/floor/add');
                      }}
                    >
                      Manage Floors
                    </button>
                    <button
                      onClick={(e) => handleDeleteFloor(e, floor.id)}
                      className="p-1.5 text-red-500 hover:bg-red-100 rounded-lg transition-colors border border-red-100 shadow-sm"
                      title="Delete Floor"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Floor Details - Right Side */}
          {selectedFloor && (
            <div className="lg:col-span-9 space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                    Teams in {selectedFloor.name}
                  </h2>
                  <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    <span className="font-bold" style={{ color: currentTheme.color }}>{selectedFloor.teams?.length || 0}</span> teams total
                  </p>
                </div>
              </div>
              {/* Floor Head and Manager */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Floor Head */}
                <div className={`rounded-lg p-6 h-full border transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Floor Head</h3>
                    <button onClick={() => handleEditClick('Head')} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
                      <Edit className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                    </button>
                  </div>
                  {selectedFloor.floorHead ? (
                    <>
                      <div className="flex items-center space-x-4 mb-4">
                        <img
                          src={getAvatarUrl(selectedFloor.floorHead.name)}
                          alt={selectedFloor.floorHead.name}
                          className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <h4 className={`font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFloor.floorHead.name}</h4>
                          <p className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedFloor.floorHeadDepartment?.name || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className={`flex items-center space-x-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <Phone className="w-4 h-4 flex-shrink-0" style={{ color: currentTheme.color }} />
                          <span className="truncate font-medium">{selectedFloor.floorHead.contactNumber || 'N/A'}</span>
                        </div>
                        <div className={`flex items-center space-x-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <Mail className="w-4 h-4 flex-shrink-0" style={{ color: currentTheme.color }} />
                          <span className="truncate font-medium">{selectedFloor.floorHead.email || 'N/A'}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className={isDark ? 'text-gray-500' : 'text-gray-500'}>No floor head assigned</p>
                  )}
                </div>

                {/* Floor Manager */}
                <div className={`rounded-lg p-6 h-full border transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Floor Manager</h3>
                    <button onClick={() => handleEditClick('Manager')} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}>
                      <Edit className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
                    </button>
                  </div>
                  {selectedFloor.floorManager ? (
                    <>
                      <div className="flex items-center space-x-4 mb-4">
                        <img
                          src={getAvatarUrl(selectedFloor.floorManager.name)}
                          alt={selectedFloor.floorManager.name}
                          className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <h4 className={`font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{selectedFloor.floorManager.name}</h4>
                          <p className={`text-sm truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedFloor.floorManagerDepartment?.name || 'N/A'}</p>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className={`flex items-center space-x-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <Phone className="w-4 h-4 flex-shrink-0" style={{ color: currentTheme.color }} />
                          <span className="truncate font-medium">{selectedFloor.floorManager.contactNumber || 'N/A'}</span>
                        </div>
                        <div className={`flex items-center space-x-2 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          <Mail className="w-4 h-4 flex-shrink-0" style={{ color: currentTheme.color }} />
                          <span className="truncate font-medium">{selectedFloor.floorManager.email || 'N/A'}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <p className={isDark ? 'text-gray-500' : 'text-gray-500'}>No floor manager assigned</p>
                  )}
                </div>
              </div>

              {/* Teams Section */}
              {selectedFloor.teams && selectedFloor.teams.length > 0 && (
                <div className={`rounded-lg p-6 border transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-200'}`}>
                  <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`}>Teams & Agents</h3>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                    {/* Left Side: Teams List */}
                    <div className="md:col-span-4 space-y-3 max-h-[500px] overflow-y-auto pr-2 custom-scrollbar">
                      <h4 className={`text-sm font-bold mb-2 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Teams</h4>
                      {selectedFloor.teams.map((team) => (
                        <button
                          key={team.id}
                          onClick={() => handleTeamClick(team)}
                          className={`w-full text-left p-4 border rounded-lg transition-all flex items-center justify-between group ${
                            selectedTeam?.id === team.id
                              ? 'shadow-sm'
                              : isDark ? 'bg-white/5 border-white/5 hover:bg-white/10' : 'bg-white border-gray-200 hover:shadow-sm'
                          }`}
                          style={{
                            backgroundColor: selectedTeam?.id === team.id ? (isDark ? 'rgba(255,255,255,0.05)' : `${currentTheme.color}15`) : undefined,
                            borderColor: selectedTeam?.id === team.id ? currentTheme.color : undefined
                          }}
                        >
                          <div>
                            <span className={`block font-bold transition-colors`} style={{ color: selectedTeam?.id === team.id ? currentTheme.color : (isDark ? '#f8fafc' : '#1F2937') }}>{team.name}</span>
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{team.memberCount || 0} members</span>
                          </div>
                          <ChevronRight className={`w-5 h-5 transition-transform ${selectedTeam?.id === team.id ? 'translate-x-1' : (isDark ? 'text-gray-600' : 'text-gray-300')}`} style={{ color: selectedTeam?.id === team.id ? currentTheme.color : undefined }} />
                        </button>
                      ))}
                    </div>

                    {/* Right Side: Agents Grid */}
                    <div className="md:col-span-8">
                      <h4 className={`text-sm font-bold mb-2 uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {selectedTeam ? `Agents in ${selectedTeam.name}` : 'Agents'}
                      </h4>
                      <div className={`rounded-lg p-4 min-h-[300px] border transition-all ${isDark ? 'bg-[#0f172a] border-white/5' : 'bg-gray-50 border-gray-100'}`}>
                        {loadingMembers ? (
                          <div className="flex flex-col items-center justify-center h-64">
                            <Loader className="w-8 h-8 animate-spin mb-2" style={{ color: currentTheme.color }} />
                            <p className={isDark ? 'text-gray-500' : 'text-gray-400'}>Loading agents...</p>
                          </div>
                        ) : selectedTeam ? (
                          teamMembers.length > 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4">
                              {teamMembers.map((member) => (
                                <div key={member.id} className={`rounded-lg p-4 flex items-center space-x-3 transition-all border ${isDark ? 'bg-white/5 border-white/5 hover:bg-white/10' : 'bg-white border-gray-200 hover:shadow-md'}`}>
                                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-lg border-2 border-white shadow-sm" style={{ backgroundColor: `${currentTheme.color}20`, color: currentTheme.color }}>
                                    {member.name?.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="min-w-0">
                                    <h5 className={`font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{member.name}</h5>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>ID: <span className={`font-bold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{member.employeeId || 'N/A'}</span></p>
                                    {member.role && <p className="text-xs truncate font-bold" style={{ color: currentTheme.color }}>{member.role}</p>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className={`flex flex-col items-center justify-center h-64 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                              <UserX className="w-12 h-12 mb-3" />
                              <p className="font-bold">No agents found</p>
                              <p className="text-sm">This team has no assigned employees.</p>
                            </div>
                          )
                        ) : (
                          <div className={`flex flex-col items-center justify-center h-64 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            <Layers className="w-12 h-12 mb-3 opacity-50" />
                            <p>Select a team to view agents</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Edit Head/Manager Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/60 flex items-start pt-20 justify-center z-[9999] backdrop-blur-sm p-4 transition-all">
          <div className={`${isDark ? 'bg-[#1e293b]' : 'bg-white'} rounded-2xl w-full max-w-md shadow-2xl overflow-visible animate-scaleIn`}>
            <div className={`px-6 py-4 border-b flex items-center justify-between rounded-t-2xl ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50'}`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Edit Floor {editRole}</h3>
              <button disabled={savingEdit} onClick={() => setShowEditModal(false)} className={`p-1 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:bg-white/10' : 'text-gray-400 hover:text-gray-700'}`}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">

              {/* Department Dropdown */}
              <div className="space-y-2 relative">
                <label className={`text-sm font-bold mb-1 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Department</label>
                <div
                  className={`w-full px-4 py-3 border rounded-xl flex items-center justify-between cursor-pointer transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                  onClick={() => setShowEditDeptDropdown(!showEditDeptDropdown)}
                >
                  <span className="font-medium">
                    {editDepartmentId ? departments.find(d => d.id === editDepartmentId)?.name : 'Select Department'}
                  </span>
                  <ChevronRight className={`w-4 h-4 text-gray-400 transform transition-transform ${showEditDeptDropdown ? 'rotate-90' : ''}`} />
                </div>
                {showEditDeptDropdown && (
                  <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto border animate-fadeIn ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'}`}>
                    {departments.map((dept) => (
                      <div
                        key={dept.id}
                        onClick={() => {
                          setEditDepartmentId(dept.id);
                          setShowEditDeptDropdown(false);
                        }}
                        className={`px-4 py-3 text-sm cursor-pointer border-b last:border-none transition-colors ${isDark ? 'text-gray-300 border-white/5 hover:bg-white/5' : 'text-gray-700 border-gray-100 hover:bg-gray-50'}`}
                      >
                        {dept.name}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Employee Dropdown */}
              <div className="space-y-2 relative">
                <label className={`text-sm font-bold mb-1 block ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Employee</label>
                <div
                  className={`w-full px-4 py-3 border rounded-xl flex items-center justify-between cursor-pointer transition-all ${isDark ? 'bg-[#0f172a] border-white/10 text-white' : 'bg-white border-gray-300 text-gray-700'}`}
                  onClick={() => setShowEditEmpDropdown(!showEditEmpDropdown)}
                >
                  <span className="font-medium">
                    {editEmployeeId ? employees.find(e => e.id === editEmployeeId)?.name : 'Select Employee'}
                  </span>
                  <ChevronRight className={`w-4 h-4 text-gray-400 transform transition-transform ${showEditEmpDropdown ? 'rotate-90' : ''}`} />
                </div>
                {showEditEmpDropdown && (
                  <div className={`absolute top-full left-0 right-0 mt-1 rounded-lg shadow-xl z-50 max-h-80 overflow-y-auto border animate-fadeIn ${isDark ? 'bg-[#1e293b] border-white/10' : 'bg-white border-gray-200'}`}>
                    <div className={`p-2 border-b sticky top-0 transition-all ${isDark ? 'bg-[#1e293b] border-white/5' : 'bg-white border-gray-100'}`}>
                      <ExpandableSearchBar
                        searchTerm={editSearchTerm}
                        onSearchChange={setEditSearchTerm}
                        placeholder="Search employee..."
                        fullWidth
                      />
                    </div>
                    {filteredEditEmployees.map((emp) => (
                      <div
                        key={emp.id}
                        onClick={() => {
                          setEditEmployeeId(emp.id);
                          setShowEditEmpDropdown(false);
                          setEditSearchTerm('');
                        }}
                        className={`px-4 py-3 text-sm cursor-pointer flex justify-between items-center border-b last:border-none transition-colors ${isDark ? 'text-gray-300 border-white/5 hover:bg-white/5' : 'text-gray-700 border-gray-100 hover:bg-gray-50'}`}
                      >
                        <div>
                          <p className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{emp.name}</p>
                          <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>{emp.employeeId || 'N/A'}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
            <div className={`px-6 py-4 border-t flex items-center justify-end gap-3 rounded-b-2xl transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
              <button disabled={savingEdit} onClick={() => setShowEditModal(false)} className={`px-5 py-2.5 font-bold rounded-lg transition-all ${isDark ? 'bg-white/5 text-gray-300 hover:bg-white/10' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                Cancel
              </button>
              <button disabled={savingEdit} onClick={handleSaveEdit} className="px-6 py-2.5 text-white font-bold hover:opacity-90 rounded-lg flex items-center gap-2 shadow-lg" style={{ backgroundColor: currentTheme.color }}>
                {savingEdit && <Loader className="w-4 h-4 animate-spin" />}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] backdrop-blur-sm p-4 transition-all">
          <div className={`${isDark ? 'bg-[#1e293b]' : 'bg-white'} rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-scaleIn`}>
            <div className={`px-6 py-4 border-b flex items-center justify-between rounded-t-2xl ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50'}`}>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Confirm Deletion</h3>
              <button disabled={loading} onClick={() => setShowDeleteModal(false)} className={`p-1 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:bg-white/10' : 'text-gray-400 hover:text-gray-700'}`}>
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <div className="flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-2 shadow-sm">
                  <Trash2 className="w-8 h-8 text-red-500" />
                </div>
                <h4 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Delete this floor?</h4>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Are you sure you want to delete this floor? This action cannot be undone.
                </p>
              </div>
            </div>
            <div className={`px-6 py-4 border-t flex items-center justify-end gap-3 transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
              <button disabled={loading} onClick={() => setShowDeleteModal(false)} className={`px-5 py-2.5 font-bold rounded-lg transition-all ${isDark ? 'bg-white/5 text-gray-300 hover:bg-white/10' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                Cancel
              </button>
              <button disabled={loading} onClick={confirmDeleteFloor} className="px-6 py-2.5 text-white font-bold bg-red-500 hover:bg-red-600 rounded-lg transition-colors flex items-center gap-2 shadow-lg shadow-red-200">
                {loading && <Loader className="w-4 h-4 animate-spin" />}
                Delete Floor
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Modal */}
      {isFilterModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm transition-all">
          <div className={`${isDark ? 'bg-[#1e293b]' : 'bg-white'} rounded-2xl w-full max-w-md overflow-hidden flex flex-col shadow-2xl animate-scaleIn`}>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/5" style={{ background: currentTheme.color }}>
              <div className="flex items-center gap-2 text-white">
                <Filter className="w-5 h-5" />
                <h2 className="text-xl font-bold">Filter Floors</h2>
              </div>
              <button
                onClick={() => setIsFilterModalOpen(false)}
                className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Team Status Filter */}
              <div>
                <label className={`flex items-center gap-2 text-sm font-bold mb-3 tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Users className={`w-4 h-4 ${isDark ? 'text-white' : 'text-teal-600'}`} />
                  TEAM STATUS
                </label>
                <div className="flex gap-2">
                  {['All', 'Has Teams', 'No Teams'].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setTempFilters({ ...tempFilters, teamStatus: opt })}
                      className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${
                        tempFilters.teamStatus === opt
                        ? (isDark ? 'border-white/40 bg-white/20 text-white' : 'border-teal-600 bg-teal-50 text-teal-700')
                        : (isDark ? 'border-white/5 text-gray-400 bg-white/5 hover:bg-white/10' : 'border-gray-200 text-gray-600 hover:bg-gray-50')
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>

              {/* Management Status Filter */}
              <div>
                <label className={`flex items-center gap-2 text-sm font-bold mb-3 tracking-wide ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <ShieldCheck className={`w-4 h-4 ${isDark ? 'text-white' : 'text-teal-600'}`} />
                  MANAGEMENT STATUS
                </label>
                <div className="flex gap-2">
                  {['All', 'Has Management', 'Missing Info'].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setTempFilters({ ...tempFilters, managementStatus: opt })}
                      className={`px-4 py-2 rounded-xl text-sm font-bold border transition-all ${
                        tempFilters.managementStatus === opt
                        ? (isDark ? 'border-white/40 bg-white/20 text-white' : 'border-teal-600 bg-teal-50 text-teal-700')
                        : (isDark ? 'border-white/5 text-gray-400 bg-white/5 hover:bg-white/10' : 'border-gray-200 text-gray-600 hover:bg-gray-50')
                      }`}
                    >
                      {opt}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className={`flex items-center justify-end gap-3 px-6 py-4 border-t rounded-b-2xl transition-all ${isDark ? 'bg-white/5 border-white/5' : 'bg-gray-50 border-gray-100'}`}>
              <button
                onClick={() => {
                  setTempFilters({
                    teamStatus: 'All',
                    managementStatus: 'All'
                  });
                }}
                className={`px-5 py-2.5 text-sm font-bold border rounded-xl transition-all ${
                  isDark ? 'bg-white/5 text-gray-300 border-white/10 hover:bg-white/10' : 'bg-white text-gray-600 border-gray-200 hover:text-gray-800'
                }`}
              >
                Reset All
              </button>
              <button
                onClick={() => {
                  setActiveFilters(tempFilters);
                  setIsFilterModalOpen(false);
                }}
                className="px-8 py-2.5 text-sm font-bold text-white rounded-xl shadow-lg transition-all"
                style={{ backgroundColor: currentTheme.color }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default FloorManagement;